import React, { forwardRef, useState } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Icon } from "@purpur/icon";
import { checkmarkBold } from "@purpur/icon/assets/checkmark-bold";
import { Label } from "@purpur/label";
import { Paragraph } from "@purpur/paragraph";
import * as Switch from "@radix-ui/react-switch";
import c from "classnames/bind";

import { DraggableX } from "./DraggableX";
import { useToggleDrag } from "./hooks/useToggleDrag";
import styles from "./toggle.module.scss";

export type ToggleProps = Omit<BaseProps<"button">, "onChange" | "children" | "id"> & {
  /**
   * To use when no label is given.
   * */
  ["aria-label"]?: string;
  /**
   * To use with custom label (not recommended).
   * */
  ["aria-labelledby"]?: string;
  /**
   * The controlled state of the toggle. Must be used in conjunction with `onChange`.
   * */
  checked?: boolean;
  /**
   * The state of the toggle when it is initially rendered. Use when you do not need to control its state.
   * */
  defaultChecked?: boolean;
  /**
   * When `true`, prevents the user from interacting with the toggle.
   * */
  disabled?: boolean;
  /**
   * When `true`, the toggle isn't draggable.
   * */
  disableDrag?: boolean;
  /**
   * ID of the toggle.
   * */
  id: string;
  /**
   * The label of the toggle.
   * */
  label?: string;
  /**
   * Set to decide which side of the toggle the label should be rendered.
   * */
  labelPosition?: "left" | "right";
  /**
   * The name of the toggle. Submitted with its owning form as part of a name/value pair, when wrapped in a form.
   * */
  name?: string;
  /**
   * Event handler called when the toggle is toggled.
   * */
  onChange?: (checked: boolean) => void;
  /**
   * When `true`, indicates that the user must check the toggle before the owning form can be submitted..
   * */
  required?: boolean;
  /**
   * The value given as data when wrapped with a form and submitted with a name.
   * */
  value?: string;
  negative?: boolean;
};

const cx = c.bind(styles);
const rootClassName = "purpur-toggle";

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      ["data-testid"]: dataTestId,
      className,
      label,
      onChange,
      labelPosition = "right",
      checked,
      disableDrag,
      defaultChecked,
      negative = false,
      ...props
    },
    ref
  ) => {
    const [internalChecked, setInternalChecked] = useState(
      typeof checked === "boolean" ? checked : !!defaultChecked
    );
    const isChecked = Boolean(typeof checked === "boolean" ? checked : internalChecked);
    const { thumbRef, trackRef, isDragging, onChangeWithDrag, ...draggableXProps } = useToggleDrag({
      checked: isChecked,
      onChange: (value) => {
        if (!props.disabled) {
          onChange?.(value);
          setInternalChecked(value);
        }
      },
    });

    const renderLabel = () => (
      <Label
        htmlFor={props.id}
        data-testid={dataTestId && `${dataTestId}-label`}
        disabled={props.disabled}
        className={cx(`${rootClassName}__label`, `${rootClassName}__label--${labelPosition}`)}
        negative={negative}
      >
        <Paragraph disabled={props.disabled} negative={negative}>
          {label}
        </Paragraph>
      </Label>
    );

    return (
      <div className={cx(className, `${rootClassName}__container`)}>
        {label && labelPosition === "left" && renderLabel()}
        <Switch.Root
          {...props}
          ref={ref}
          id={props.id}
          data-testid={dataTestId}
          className={cx(rootClassName, { [`${rootClassName}--negative`]: negative })}
          onCheckedChange={onChangeWithDrag}
          checked={isChecked}
        >
          <span
            ref={trackRef}
            className={cx(`${rootClassName}__track`, {
              [`${rootClassName}__track--negative`]: negative,
            })}
          >
            <span className={cx(`${rootClassName}__checkmark-container`)}>
              <Icon className={cx(`${rootClassName}__checkmark`)} svg={checkmarkBold} size="xxs" />
            </span>
            <DraggableX disabled={disableDrag} {...draggableXProps}>
              <Switch.Thumb
                ref={thumbRef}
                data-testid={dataTestId && `${dataTestId}-thumb`}
                className={cx(`${rootClassName}__thumb`, {
                  [`${rootClassName}__thumb--dragging`]: isDragging,
                  [`${rootClassName}__thumb--negative`]: negative,
                })}
              />
            </DraggableX>
          </span>
        </Switch.Root>
        {label && labelPosition === "right" && renderLabel()}
      </div>
    );
  }
);

Toggle.displayName = "Toggle";
